import { useEffect } from 'react'
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { FixedAssetListItem, ScheduleMonthItem } from '@/services/fixedAssets'
import FixedAssetBookValueChart from './FixedAssetBookValueChart'
import { formatFixedAssetMoney } from './fixedAssetsFormat'

type YearGroup = {
  year: number
  expenseTotal: number
  lastAccum: string
  lastNbv: string
  months: ScheduleMonthItem[]
}

function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

interface FixedAssetDetailDrawerProps {
  asset: FixedAssetListItem | null
  open: boolean
  onClose: () => void
  canEdit: boolean
  scheduleLoading: boolean
  scheduleMonths: ScheduleMonthItem[]
  yearGroups: YearGroup[]
  expandedYears: Set<number>
  onToggleYear: (year: number) => void
  postedMonthKeys: Set<string>
  accountLabel: (id: string) => string
  onEdit: (asset: FixedAssetListItem) => void
  onDelete: (asset: FixedAssetListItem) => void
}

export default function FixedAssetDetailDrawer({
  asset,
  open,
  onClose,
  canEdit,
  scheduleLoading,
  scheduleMonths,
  yearGroups,
  expandedYears,
  onToggleYear,
  postedMonthKeys,
  accountLabel,
  onEdit,
  onDelete,
}: FixedAssetDetailDrawerProps) {
  useEffect(() => {
    if (!open) return
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onClose])

  if (!open || !asset) return null

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/10"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed right-0 top-0 z-50 flex h-full w-[min(720px,92vw)] flex-col border-l border-gray-200 bg-white shadow-xl"
        aria-label={`Fixed asset details for ${asset.name}`}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900">{asset.name}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {asset.asset_class} · {asset.location}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {canEdit ? (
              <>
                <Button variant="outline" onClick={() => onEdit(asset)}>
                  Edit
                </Button>
                <Button variant="danger" onClick={() => void onDelete(asset)}>
                  <TrashIcon className="mr-1 h-4 w-4" aria-hidden />
                  Delete
                </Button>
              </>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-gray-500">Purchase date</dt>
              <dd>{asset.purchase_date.slice(0, 10)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">In-service date</dt>
              <dd>{asset.dep_start_date.slice(0, 10)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Useful life</dt>
              <dd>
                {asset.non_depreciable
                  ? 'Non-depreciable'
                  : `${asset.useful_life_years} years`}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Cost / salvage</dt>
              <dd className="tabular-nums">
                {formatFixedAssetMoney(asset.purchase_price)} /{' '}
                {formatFixedAssetMoney(asset.salvage_value)}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Asset account</dt>
              <dd>{accountLabel(asset.asset_account_qbo_id)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Expense account</dt>
              <dd>{accountLabel(asset.expense_account_qbo_id)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Accumulated account</dt>
              <dd>{accountLabel(asset.accumulated_account_qbo_id)}</dd>
            </div>
          </dl>

          {asset.non_depreciable ? (
            <p className="mt-6 text-sm text-gray-500">
              This asset is non-depreciable — no depreciation schedule or chart.
            </p>
          ) : scheduleLoading ? (
            <p className="mt-6 text-sm text-gray-500">Loading schedule…</p>
          ) : (
            <div className="mt-6 space-y-6">
              <FixedAssetBookValueChart
                purchaseDate={asset.purchase_date}
                purchasePrice={asset.purchase_price}
                salvageValue={asset.salvage_value}
                months={scheduleMonths}
              />

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="table-forecast min-w-full border-separate border-spacing-0">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th className="text-right">Expense</th>
                      <th className="text-right">Accumulated</th>
                      <th className="text-right">NBV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearGroups.flatMap((group) => {
                      const expanded = expandedYears.has(group.year)
                      const yearRow = (
                        <tr
                          key={`year-${group.year}`}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => onToggleYear(group.year)}
                        >
                          <td className="font-medium text-gray-900">
                            {group.year}
                            <span className="ml-2 text-xs text-gray-500">
                              {expanded ? '▾' : '▸'}
                            </span>
                          </td>
                          <td className="tabular-nums text-right">
                            {formatFixedAssetMoney(group.expenseTotal)}
                          </td>
                          <td className="tabular-nums text-right">
                            {formatFixedAssetMoney(group.lastAccum)}
                          </td>
                          <td className="tabular-nums text-right">
                            {formatFixedAssetMoney(group.lastNbv)}
                          </td>
                        </tr>
                      )

                      if (!expanded) return [yearRow]

                      const monthRows = group.months.map((month) => {
                        const key = monthKey(month.year, month.month)
                        const posted = postedMonthKeys.has(key)
                        return (
                          <tr key={key} className={posted ? 'bg-emerald-50/60' : undefined}>
                            <td className={posted ? 'text-gray-900' : 'text-gray-400'}>
                              {new Date(month.year, month.month - 1, 1).toLocaleString('en-US', {
                                month: 'short',
                              })}
                            </td>
                            <td
                              className={`tabular-nums text-right ${
                                posted ? 'text-gray-900' : 'text-gray-400'
                              }`}
                            >
                              {formatFixedAssetMoney(month.expense)}
                            </td>
                            <td
                              className={`tabular-nums text-right ${
                                posted ? 'text-gray-900' : 'text-gray-400'
                              }`}
                            >
                              {formatFixedAssetMoney(month.expected_accum)}
                            </td>
                            <td
                              className={`tabular-nums text-right ${
                                posted ? 'text-gray-900' : 'text-gray-400'
                              }`}
                            >
                              {formatFixedAssetMoney(month.nbv)}
                            </td>
                          </tr>
                        )
                      })

                      return [yearRow, ...monthRows]
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  )
}
